import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { motion } from 'framer-motion';
import { ShieldCheck, Compass, Award, Users, ArrowUpRight, Play, Anchor, Instagram, MessageCircle, Star, Facebook } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { toEmbedUrl } from '../lib/youtube';
import { SectionHeader } from '../components/SectionHeader';

const VIDEO_FILE_RX = /\.(mp4|webm|ogg|mov)(\?|$)/i;

const pillars = [
  { icon: ShieldCheck, title: 'Internationally Certified', body: 'SDI, TDI, PADI & CMAS training to global standards — from your first breath underwater to advanced tech diving.' },
  { icon: Compass, title: 'Local Expertise', body: 'Dubai-based instructors with deep knowledge of UAE and Oman dive sites — we know where to take you.' },
  { icon: Award, title: 'Small Groups', body: 'Personal attention, careful mentorship, and a low instructor-to-student ratio on every program.' },
  { icon: Users, title: 'Built for Everyone', body: 'From age 8 with our Future Buddies program, to seasoned divers pursuing Instructor Trainer.' },
];

export default function Home() {
  const { settings } = useSettings();
  const [featuredTrip, setFeaturedTrip] = useState(null);
  const [coreCourses, setCoreCourses] = useState([]);

  useEffect(() => {
    api.get('/trips').then(({ data }) => setFeaturedTrip(data?.[0] || null)).catch(() => {});
    api.get('/courses?agency=SDI').then(({ data }) => {
      const map = new Map((data || []).map((c) => [c.slug, c]));
      const path = ['open-water-scuba-diver', 'advanced-diver', 'rescue-diver', 'sdi-divemaster']
        .map((s) => map.get(s)).filter(Boolean);
      setCoreCourses(path);
    }).catch(() => {});
  }, []);

  const heroVideoUrl = settings?.hero_video_url || '';
  const isVideoFile = VIDEO_FILE_RX.test(heroVideoUrl);
  const heroEmbed = !isVideoFile && heroVideoUrl ? toEmbedUrl(heroVideoUrl) : '';

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[100svh] overflow-hidden">
        {/* Video background — wrapped in pointer-events-none container, scaled extra-large to push YouTube branding off-screen */}
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          {isVideoFile ? (
            <video
              data-testid="home-hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 object-cover"
              style={{
                width: 'max(100vw, calc(100vh * 16 / 9))',
                height: 'max(100vh, calc(100vw * 9 / 16))',
              }}
            >
              <source src={heroVideoUrl} type="video/mp4" />
            </video>
          ) : heroEmbed ? (
            <div className="absolute inset-0 overflow-hidden">
              <iframe
                data-testid="home-hero-youtube"
                src={heroEmbed}
                title="Arab Diver Tours background video"
                allow="autoplay; encrypted-media; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
                tabIndex={-1}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{
                  width: 'max(140vw, calc(140vh * 16 / 9))',
                  height: 'max(140vh, calc(140vw * 9 / 16))',
                }}
                frameBorder="0"
              />
            </div>
          ) : settings?.hero_poster_url ? (
            <img src={settings.hero_poster_url} alt="Underwater" className="absolute inset-0 w-full h-full object-cover" />
          ) : null}
          {/* Strong overlays: tint + brand gradient + extra darken at sides to mask any visible YT controls */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/70 to-[#0a1628]/35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/40 via-transparent to-[#0a1628]/55" />
          {/* Hard masks at very top/bottom edges to cover any YouTube branding that survives the scaling */}
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0a1628] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0a1628] to-transparent" />
          <div className="absolute inset-0 hero-radial" />
        </div>
        {/* Click blocker overlay so users can't reach the YT iframe even if pointer-events were enabled */}
        <div className="absolute inset-0 z-[1]" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[100svh] flex flex-col justify-center pt-24 pb-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: 'easeOut' }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-[#c8e600] animate-pulse" /> SDI • TDI • PADI • CMAS Certified Training
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              {settings?.hero_headline || 'Discover the Deep with Arab Diver Tours'}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed">
              {settings?.hero_subheadline || 'Internationally certified SDI & TDI scuba training, premium Oman dive trips, and expert gear — all from Dubai, UAE.'}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/courses/sdi">
                <Button data-testid="home-hero-start-learning-button" size="lg" className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold rounded-xl h-12 px-6">
                  Start Learning
                </Button>
              </Link>
              <Link to="/trips">
                <Button data-testid="home-hero-book-trip-button" size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl h-12 px-6">
                  <Anchor className="mr-2 h-4 w-4" /> Book a Trip
                </Button>
              </Link>
              {/* Quick contact icons */}
              <div className="flex items-center gap-2 ml-1">
                {settings?.whatsapp_url && (
                  <a
                    data-testid="home-hero-whatsapp"
                    href={settings.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp us"
                    className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-[#25D366] text-white hover:bg-[#1ebe57] transition-colors shadow-md shadow-black/20"
                  >
                    <MessageCircle className="h-5 w-5" />
                  </a>
                )}
                {settings?.instagram_url && (
                  <a
                    data-testid="home-hero-instagram"
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Instagram"
                    className="h-12 w-12 inline-flex items-center justify-center rounded-xl text-white hover:opacity-90 transition-opacity shadow-md shadow-black/20"
                    style={{ background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)' }}
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {settings?.facebook_url && (
                  <a
                    data-testid="home-hero-facebook"
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Facebook"
                    className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-[#1877F2] text-white hover:bg-[#1465d4] transition-colors shadow-md shadow-black/20"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-3xl">
              {[
                { k: settings?.stat_years || '25+', v: 'Years Experience', href: null },
                { k: settings?.stat_divers || '2000+', v: 'Divers Trained', href: null },
                { k: settings?.stat_courses || '500+', v: 'Courses Offered', href: null },
                { k: settings?.stat_rating || '5★', v: 'TripAdvisor', href: settings?.tripadvisor_url || null, accent: '#34e0a1' },
                { k: 'Google', v: 'Read Reviews', href: settings?.google_review_url || null, accent: '#fbbc05', isGoogle: true },
              ].map((s) => {
                const inner = (
                  <>
                    <div className="font-display text-2xl font-semibold flex items-center gap-1.5" style={{ color: s.accent || '#c8e600' }}>
                      {s.isGoogle ? (
                        <span className="font-display text-2xl tracking-tight"><span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span><span className="text-[#FBBC05]">o</span><span className="text-[#4285F4]">g</span><span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span></span>
                      ) : s.k}
                      {s.href && <ArrowUpRight className="h-3.5 w-3.5" style={{ color: s.accent ? s.accent : '#c8e600', opacity: 0.85 }} />}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55 mt-0.5">{s.v}</div>
                  </>
                );
                return s.href ? (
                  <a
                    key={s.v}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={s.isGoogle ? "home-google-review-link" : "home-tripadvisor-link"}
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur hover:border-white/30 hover:bg-white/10 transition-colors"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={s.v} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur">
                    {inner}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/55 text-[11px] uppercase tracking-[0.24em] flex flex-col items-center gap-2">
          Scroll
          <div className="h-8 w-px bg-gradient-to-b from-white/50 to-transparent" />
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Why Choose Us" title="Diving done properly. From day one." subtitle="Whether you're stepping into the water for the first time or pushing into technical depths, Arab Diver Tours is built around expert instruction, intelligent dive planning, and a genuine respect for the ocean." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map(({ icon: Icon, title, body }) => (
              <motion.div key={title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                <Card className="h-full rounded-2xl border-white/10 bg-white/5 backdrop-blur hover:border-[#00b4d8]/40 transition-colors">
                  <div className="p-6">
                    <div className="h-11 w-11 rounded-xl bg-[#00b4d8]/15 text-[#00b4d8] inline-flex items-center justify-center mb-4"><Icon className="h-5 w-5" /></div>
                    <div className="font-display text-lg font-semibold">{title}</div>
                    <p className="mt-2 text-sm text-white/65 leading-relaxed">{body}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSE TRACK PREVIEW */}
      <section className="py-16 sm:py-20 bg-[#07101f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <SectionHeader eyebrow="Recreational Pathway" title="From your first dive to Divemaster" subtitle="Every great dive career follows a path. Here are the core SDI milestones we'll guide you through." />
            <div className="flex gap-3">
              <Link to="/courses/sdi"><Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl">View Full SDI Tree <ArrowUpRight className="ml-1 h-4 w-4" /></Button></Link>
              <Link to="/courses/tdi"><Button className="bg-[#00b4d8] text-[#0a1628] hover:bg-[#0098b8] rounded-xl">Explore TDI <ArrowUpRight className="ml-1 h-4 w-4" /></Button></Link>
            </div>
          </div>
          <div className="mt-10 grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-4">
            {coreCourses.length === 0 && (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-44 rounded-2xl bg-white/5 border border-white/10 animate-pulse" />
              ))
            )}
            {coreCourses.map((c, i) => (
              <Link to="/courses/sdi" key={c.id} className="group relative rounded-2xl border border-white/10 bg-white/5 p-5 hover:border-[#00b4d8]/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8]">Step {i + 1}</div>
                  <ArrowUpRight className="h-4 w-4 text-white/55 group-hover:text-[#00b4d8] transition-colors" />
                </div>
                <div className="mt-3 font-display text-lg font-semibold leading-snug">{c.short_name}</div>
                <div className="mt-2 text-xs text-white/60 line-clamp-3">{(c.description || '').slice(0, 140)}…</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TRIP */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Oman Trips" title="Cross the border. Cruise the fjords." />
          {featuredTrip ? (
            <Link to={`/trips/${featuredTrip.slug}`} className="mt-10 group block overflow-hidden rounded-3xl border border-white/10 bg-white/5">
              <div className="grid lg:grid-cols-5">
                <div className="relative lg:col-span-3 aspect-[16/10] lg:aspect-auto">
                  <img src={featuredTrip.image_url} alt={featuredTrip.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0a1628]/85 via-[#0a1628]/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-[#0a1628]/70 backdrop-blur px-3 py-1.5 text-xs">
                    <Play className="h-3.5 w-3.5" /> Featured trip
                  </div>
                </div>
                <div className="p-8 lg:p-12 lg:col-span-2 flex flex-col justify-center">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600]">{featuredTrip.duration}</div>
                  <h3 className="mt-2 font-display text-2xl lg:text-3xl font-semibold">{featuredTrip.name}</h3>
                  <p className="mt-3 text-white/65 text-sm leading-relaxed">{featuredTrip.short_description}</p>
                  <div className="mt-6 flex items-center gap-4">
                    <span className="font-display text-2xl font-semibold text-white">{featuredTrip.price}</span>
                    <span className="text-xs text-white/55">per person</span>
                  </div>
                  <div className="mt-6">
                    <Button className="bg-[#00b4d8] text-[#0a1628] hover:bg-[#0098b8] rounded-xl">Read More <ArrowUpRight className="ml-1 h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </Link>
          ) : (
            <div className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-10 text-white/65">Trips coming soon.</div>
          )}
        </div>
      </section>
    </>
  );
}
